import { useTrans } from '@/shared/hooks/useTranslation';
import { SupportTicketForm } from '@/view/features/support/SupportTicketForm';
import { Button } from '@/view/shared/Button';
import { Drawer } from '@/view/shared/Drawer';
import { useSupportPanelViewModel } from '@/viewmodel/features/support';
import { CalendarDays, CheckCircle, ExternalLink, TicketCheck } from 'lucide-react';

interface SupportPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SupportPanel({ isOpen, onClose }: Readonly<SupportPanelProps>) {
  const { t } = useTrans();
  const { step, ticket, scheduleUrl, show, ticketCreated, close } =
    useSupportPanelViewModel(onClose);

  return (
    <Drawer
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) close();
      }}
      title={t('support.title')}
    >
      {step === 'menu' && (
        <div className="flex flex-col gap-3 pt-2">
          <button
            type="button"
            onClick={() => show('schedule')}
            className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-surface-secondary transition-colors text-left cursor-pointer"
          >
            <CalendarDays size={20} className="text-primary shrink-0" />
            <div>
              <p className="font-medium text-sm">{t('support.scheduleCall')}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('support.scheduleCallDescription')}
              </p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => show('ticket')}
            className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-surface-secondary transition-colors text-left cursor-pointer"
          >
            <TicketCheck size={20} className="text-primary shrink-0" />
            <div>
              <p className="font-medium text-sm">{t('support.createTicket')}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('support.createTicketDescription')}
              </p>
            </div>
          </button>
        </div>
      )}

      {step === 'schedule' && (
        <div className="flex flex-col gap-3 pt-2">
          <button
            type="button"
            onClick={() => show('menu')}
            className="self-start text-sm text-muted-foreground cursor-pointer"
          >
            ← {t('common.back')}
          </button>
          {scheduleUrl ? (
            <iframe
              src={scheduleUrl}
              title={t('support.scheduleCall')}
              className="h-125 w-full rounded-lg border border-border"
            />
          ) : (
            <p className="text-sm text-muted-foreground">{t('support.calendlyNotConfigured')}</p>
          )}
        </div>
      )}

      {step === 'ticket' && (
        <div className="flex flex-col gap-3 pt-2">
          <button
            type="button"
            onClick={() => show('menu')}
            className="self-start text-sm text-muted-foreground cursor-pointer"
          >
            ← {t('common.back')}
          </button>
          <SupportTicketForm onSuccess={ticketCreated} />
        </div>
      )}

      {step === 'success' && ticket && (
        <div className="flex flex-col items-center gap-4 pt-8 text-center">
          <CheckCircle size={48} className="text-success" />
          <div>
            <p className="font-semibold text-base">{t('support.successTitle')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {t('support.successBody', { issueNumber: String(ticket.issueNumber) })}
            </p>
          </div>
          <a
            href={ticket.issueUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            {t('support.viewTicket')}
            <ExternalLink size={13} />
          </a>
          <Button variant="outline" onClick={close} className="mt-2">
            {t('common.close')}
          </Button>
        </div>
      )}
    </Drawer>
  );
}
